import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../contexts/ThemeContext';

import { authFetch } from '../../services/authFetch';

// Notification Toast styled components
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const AbsentPermitForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    from_date: '',
    to_date: '',
    purpose: '',
    destination: '',
    pic: '',
    relationship: '',
    pic_contact_number: '',
    activity: 'Teori', // Default to 'Teori'
    images: []
  });

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData({
        from_date: initialData.from_date || '',
        to_date: initialData.to_date || '',
        purpose: initialData.purpose || '',
        destination: initialData.destination || '',
        pic: initialData.pic || '',
        relationship: initialData.relationship || '',
        pic_contact_number: initialData.pic_contact_number || '',
        activity: initialData.activity || 'Teori',
        // Only set images if they are actual File objects, not API response objects
        images: readOnly ? (initialData.images || []) : []
      });
    }
  }, [initialData, readOnly]);

  const handleFileChange = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setNotification({ show: true, message: 'Hanya file gambar yang diperbolehkan.', type: 'error' });
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        imageFiles.map(async (file) => {
          try {
            // Mobile-optimized compression settings
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const options = {
              maxSizeMB: isMobile ? 0.3 : 0.5, // Smaller size for mobile
              maxWidthOrHeight: isMobile ? 800 : 1024, // Lower resolution for mobile
              useWebWorker: true,
              fileType: 'image/jpeg', // Force JPEG for better compression
              initialQuality: 0.8
            };
            
            let compressedFile;
            if (file.size > (isMobile ? 512 * 1024 : 1024 * 1024)) {
              compressedFile = await imageCompression(file, options);
              
              // Fallback compression if still too large
              if (compressedFile.size > (isMobile ? 300 * 1024 : 500 * 1024)) {
                const fallbackOptions = {
                  ...options,
                  maxSizeMB: isMobile ? 0.2 : 0.3,
                  maxWidthOrHeight: isMobile ? 600 : 800,
                  initialQuality: 0.6
                };
                compressedFile = await imageCompression(compressedFile, fallbackOptions);
              }
            } else {
              compressedFile = file;
            }
            
            // Ensure the result is a proper File object, not a Blob
            if (compressedFile instanceof Blob && !(compressedFile instanceof File)) {
              const fileName = file.name || `compressed_image_${Date.now()}.jpg`;
              compressedFile = new File([compressedFile], fileName, {
                type: compressedFile.type || 'image/jpeg',
                lastModified: Date.now()
              });
            }
            
            return compressedFile;
          } catch (compressionError) {
            console.error('Error during image compression:', compressionError);
            // Return original file if compression fails
            return file;
          }
        })
      );

      const validFiles = compressedFiles.filter(file => file !== null);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles].slice(0, 5)
      }));
      
      // Clear the input to allow re-selecting the same file
      e.target.value = '';
      
    } catch (error) {
      console.error('Error processing images:', error);
      setNotification({ 
        show: true, 
        message: 'Gagal memproses gambar. Silakan coba lagi.', 
        type: 'error' 
      });
    }
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    if (name !== 'images') {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleActivityChange = (type) => {
    if (readOnly) return;
    setFormData({
      ...formData,
      activity: type
    });
  };

  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    
    // Validate that at least one image is uploaded
    if (!formData.images || formData.images.length === 0) {
      setNotification({ 
        show: true, 
        message: 'Wajib upload minimal 1 foto surat / foto pendukung.', 
        type: 'error' 
      });
      return;
    }
    
    setNotification({ show: false, message: '', type: 'success' });
    setLoading(true);
    const dataToSend = new FormData();
    dataToSend.append('from_date', formData.from_date);
    dataToSend.append('to_date', formData.to_date);
    dataToSend.append('purpose', formData.purpose);
    dataToSend.append('destination', formData.destination);
    dataToSend.append('pic', formData.pic);
    dataToSend.append('relationship', formData.relationship);
    dataToSend.append('pic_contact_number', formData.pic_contact_number || '');
    dataToSend.append('activity', formData.activity);
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => {
        // Ensure we're sending File objects, not Blobs
        if (file instanceof Blob && !(file instanceof File)) {
          const fileName = `sickness_image_${Date.now()}.jpg`;
          const fileObject = new File([file], fileName, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now()
          });
          dataToSend.append('images[]', fileObject);
        } else if (file instanceof File) {
          dataToSend.append('images[]', file);
        } else {
          console.warn('Invalid file type detected:', typeof file);
        }
      });
    }
    try {
      const response = await authFetch('/api/absent_permits', {
        method: 'POST',
        body: dataToSend
      });
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setNotification({ show: true, message: data.message || 'Gagal mengirim permohonan tidak masuk.', type: 'error' });
      } else {
        setNotification({ show: true, message: data.message || 'Permohonan Tidak Masuk berhasil diajukan!', type: 'success' });
        setFormData({
          from_date: '',
          to_date: '',
          purpose: '',
          destination: '',
          pic: '',
          relationship: '',
          pic_contact_number: '',
          activity: 'Teori',
          images: []
        });
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan.', type: 'error' });
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>Formulir Permohonan Tidak Masuk</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>
      <FormContent>
        {notification.show && (
          <NotificationToast $type={notification.type}>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>&times;</CloseNotificationButton>
          </NotificationToast>
        )}
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <TypeToggleContainer>
            <TypeToggleLabel>Pilih Kelas</TypeToggleLabel>
            <TypeToggleButtonGroup>
              <TypeToggleButton 
                type="button" 
                active={formData.activity === 'Teori' ? 'true' : 'false'}
                onClick={() => handleActivityChange('Teori')}
                disabled={readOnly}
              >
                Teori
              </TypeToggleButton>
              <TypeToggleButton 
                type="button" 
                active={formData.activity === 'Praktek' ? 'true' : 'false'}
                onClick={() => handleActivityChange('Praktek')}
                disabled={readOnly}
              >
                Praktek
              </TypeToggleButton>
            </TypeToggleButtonGroup>
          </TypeToggleContainer>
          
          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="from_date">Dari tanggal</label>
              <input
                type="date"
                id="from_date"
                name="from_date"
                value={formData.from_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="to_date">Sampai</label>
              <input
                type="date"
                id="to_date"
                name="to_date"
                value={formData.to_date}
                min={formData.from_date || new Date().toISOString().split('T')[0]}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="purpose">Ada keperluan apa</label>
            <textarea
              id="purpose"
              name="purpose"
              rows="3"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Tuliskan alasan/keperluan tidak masuk"
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
          </FormGroup>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="destination">Nama tempat dan alamat tujuan</label>
            <textarea
              id="destination"
              name="destination"
              rows="2"
              value={formData.destination}
              onChange={handleChange}
              placeholder="Contoh: Rumah Sakit Sehat, Jl. Pahlawan No. 10"
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
          </FormGroup>

          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="pic">Nama penanggungjawab</label>
              <input
                type="text"
                id="pic"
                name="pic"
                value={formData.pic}
                onChange={handleChange}
                placeholder="Contoh: Budi Santoso"
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>

            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="relationship">Hubungan</label>
              <input
                type="text"
                id="relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                placeholder="Contoh: Orang tua, Wali, Saudara"
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="pic_contact_number">Nomor telepon penanggungjawab</label>
            <input
              type="tel"
              id="pic_contact_number"
              name="pic_contact_number"
              value={formData.pic_contact_number}
              onChange={handleChange}
              placeholder="Contoh: 081234567890"
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="images">
              Upload foto surat & foto pendukung (Bisa lebih dari 1) <RequiredAsterisk>*</RequiredAsterisk>
            </label>
            <input
              type="file"
              id="images"
              name="images"
              multiple
              onChange={handleFileChange}
              accept="image/*"
              disabled={readOnly}
            />
            {!readOnly && formData.images.length === 0 && (
              <HelperText>Minimal 1 foto wajib diupload</HelperText>
            )}
            <ImagePreviewContainer>
              {formData.images && formData.images.map((image, index) => {
                // Handle different image formats
                let imgSrc = '';
                if (image instanceof File) {
                  imgSrc = URL.createObjectURL(image);
                } else if (typeof image === 'string') {
                  imgSrc = image;
                } else if (image && image.image_url) {
                  // API returns image objects with image_url property
                  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                  imgSrc = image.image_url.startsWith('http') 
                    ? image.image_url 
                    : `${baseUrl}${image.image_url.startsWith('/') ? '' : '/'}${image.image_url}`;
                } else if (image && image.url) {
                  imgSrc = image.url;
                }
                
                return (
                  <ImagePreview key={index}>
                    <img 
                      src={imgSrc} 
                      alt={`preview ${index}`}
                      onError={(e) => {
                        console.error('Image failed to load:', imgSrc);
                        e.target.style.display = 'none';
                      }}
                    />
                    {!readOnly && (
                      <RemoveImageButton onClick={() => setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }))}>×</RemoveImageButton>
                    )}
                  </ImagePreview>
                );
              })}
            </ImagePreviewContainer>
          </FormGroup>
          <NoteBox>
            <NoteIcon>ℹ️</NoteIcon>
            <NoteText>Catatan: Wajib membawa surat keterangan dari penanggung jawab dan menyerahkannya ke pamong saat masuk.</NoteText>
          </NoteBox>

          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Mengirim...' : 'Kirim Permohonan'}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch (props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${ImagePreview}:hover & {
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
  
  form {
    max-width: 800px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const NoteBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: rgba(43, 93, 161, 0.1);
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const NoteIcon = styled.div`
  font-size: 1.25rem;
`;

const NoteText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #6c757d;
    
    &:hover {
      background-color: #6c757d;
    }
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const TypeToggleContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TypeToggleLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TypeToggleButton = styled.button`
  background-color: ${props => props.active === 'true' ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active === 'true' ? 'white' : 'var(--text-dark)'};
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.active === 'true' ? 'var(--primary-light)' : 'var(--background-light)'};
  }
`;

const RequiredAsterisk = styled.span`
  color: #dc3545;
  margin-left: 4px;
  font-weight: 600;
`;

const HelperText = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #dc3545;
  font-style: italic;
`;

export default AbsentPermitForm;